/**
 * Work Types Settings
 *
 * Workspace admins define custom work types (execution recipes) and metadata.
 * Built-in types are read-only (Canon Doc 21 §2.2). Custom types are workspace-scoped (§2.3).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Wrench,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  Lock,
  ChevronDown,
  X,
  Check,
  History,
} from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useExecutionRecipeStore } from '@/stores/prioritization/executionRecipeStore';
import { useResourceTypesStore } from '@/stores/prioritization/resourceTypesStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { isPlatformOrSystemAdmin } from '@/types/prioritization/roles';
import {
  APPROACH_KIND_LABELS,
  APPROACH_KIND_OPTIONS,
  BUILTIN_WORK_TYPE_OPTIONS,
  WORK_TYPE_LABELS,
  validateResourceAllocations,
  type ApproachKind,
  type ExecutionRecipeTemplate,
  type ResourceAllocation,
} from '@/types/work-type-recipes';
import type { CustomWorkTypeInput } from '@/lib/executionRecipeService';

const EMPTY_FORM: CustomWorkTypeInput = {
  work_type_ref: '',
  name: '',
  approach_kind: 'incremental',
  uncertainty_multiplier: 1.0,
  description: '',
  resource_allocations: [],
  metadata: { intake_hint: '' },
  active: true,
};

/** Compact one-line summary of a recipe's demand template. */
function allocationsSummary(allocations: ResourceAllocation[], labelFor: (k: string) => string): string {
  if (!allocations || allocations.length === 0) return '';
  return allocations.map((a) => `${labelFor(a.resourceType)} ${a.workUnits} WU`).join(' · ');
}

function slugifyWorkTypeRef(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

function RecipeCard({
  recipe,
  readOnly,
  shadowed,
  onEdit,
  onDelete,
  onToggleActive,
  onHistory,
  resourceLabel,
}: {
  recipe: ExecutionRecipeTemplate;
  readOnly?: boolean;
  shadowed?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleActive?: (active: boolean) => void;
  onHistory?: () => void;
  resourceLabel: (key: string) => string;
}) {
  const label = recipe.is_builtin
    ? WORK_TYPE_LABELS[recipe.work_type_ref as keyof typeof WORK_TYPE_LABELS] ?? recipe.name
    : recipe.name;

  return (
    <div
      className={` border p-4 ${
        recipe.active === false ? 'bg-[var(--surface)] border-[var(--rule)] opacity-70' : 'bg-[var(--bg-raised)] border-[var(--rule)]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-[var(--text-pri)] m-0">{label}</h3>
            <code className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-sec)]">
              {recipe.work_type_ref}
            </code>
            {recipe.is_builtin ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--text-ter)]">
                <Lock className="h-3 w-3" /> Built-in
              </span>
            ) : (
              <span className="text-[11px] font-medium text-[var(--text-ter)]">Custom</span>
            )}
            {!recipe.active && (
              <span className="text-[11px] font-medium text-[var(--mustard)]">Disabled</span>
            )}
            {shadowed && (
              <span className="text-[11px] font-medium text-[var(--text-ter)]" title="A workspace version of this built-in exists under Custom Types and governs here">
                Customized below
              </span>
            )}
            {!recipe.is_builtin && recipe.version_number > 1 && (
              <button
                type="button"
                onClick={onHistory}
                disabled={!onHistory}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:hover:text-[var(--text-ter)]"
                title="View version history"
              >
                <History className="h-3 w-3" /> v{recipe.version_number}
              </button>
            )}
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-1 mb-2">
            {APPROACH_KIND_LABELS[recipe.approach_kind]} · ×{recipe.uncertainty_multiplier} uncertainty
          </p>
          {recipe.resource_allocations && recipe.resource_allocations.length > 0 ? (
            <p className="text-xs text-[var(--text-sec)] mb-2 m-0">
              <span className="font-medium text-[var(--text-ter)]">Demand:</span>{' '}
              {allocationsSummary(recipe.resource_allocations, resourceLabel)}
            </p>
          ) : (
            /* An empty demand template used to render as nothing at all, which
               is how every work type in production came to have one without
               anyone noticing. It is the one field that decides whether work of
               this type can be measured against capacity, so its absence is
               stated rather than left silent. */
            <p className="text-xs text-[var(--mustard)] mb-2 m-0">
              <span className="font-medium">No demand template.</span>{' '}
              Work of this type cannot be measured against capacity — commitments using it
              show no resource requirement, so they can never read as backed.
            </p>
          )}
          {recipe.description && (
            <p className="text-sm text-[var(--text-sec)] m-0">{recipe.description}</p>
          )}
          {typeof recipe.metadata?.intake_hint === 'string' && recipe.metadata.intake_hint && (
            <p className="text-xs text-[var(--text-ter)] mt-2 m-0">
              Intake hint: {recipe.metadata.intake_hint as string}
            </p>
          )}
          {recipe.required_process_artifacts.length > 0 && (
            <p className="text-xs text-[var(--text-ter)] mt-2 m-0">
              {recipe.required_process_artifacts.filter((a) => a.blocking).length} blocking artifact
              {recipe.required_process_artifacts.filter((a) => a.blocking).length === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {onToggleActive && (
              <label className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)] cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-[var(--rule)]"
                  checked={recipe.active}
                  onChange={(e) => onToggleActive(e.target.checked)}
                />
                Active
              </label>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                title={recipe.is_builtin ? 'Customize for this workspace' : 'Edit'}
                className="p-1.5 hover:bg-[var(--hover-tint)] text-[var(--text-ter)]"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button type="button" onClick={onDelete} className="p-1.5 hover:bg-[var(--coral-dim)] text-[var(--coral)]">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WorkTypesSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const recipes = useExecutionRecipeStore((s) => s.recipes);
  const loading = useExecutionRecipeStore((s) => s.loading);
  const fetchRecipes = useExecutionRecipeStore((s) => s.fetchRecipes);
  const createCustom = useExecutionRecipeStore((s) => s.createCustom);
  const updateCustom = useExecutionRecipeStore((s) => s.updateCustom);
  const removeCustom = useExecutionRecipeStore((s) => s.removeCustom);
  const addNotification = useUIStore((s) => s.addNotification);
  const can = useUserStore((s) => s.can);
  const currentRole = useUserStore((s) => s.currentRole);
  const isAdmin = currentRole ? isPlatformOrSystemAdmin(currentRole) : false;
  const canEdit = can('admin.settings') || isAdmin;

  const loadVersions = useExecutionRecipeStore((s) => s.loadVersions);
  const loadResourceTypes = useResourceTypesStore((s) => s.load);
  const getEnabledResourceTypes = useResourceTypesStore((s) => s.getEnabledTypes);
  const allResourceTypes = useResourceTypesStore((s) => s.getAllTypes);

  const resourceLabel = (key: string) =>
    allResourceTypes().find((rt) => rt.key === key)?.label ?? key;

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingIsBuiltin =
    editingId !== null && recipes.some((r) => r.id === editingId && r.is_builtin);
  const [form, setForm] = useState<CustomWorkTypeInput>(EMPTY_FORM);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [refTouched, setRefTouched] = useState(false);
  const [historyFor, setHistoryFor] = useState<ExecutionRecipeTemplate | null>(null);
  const [versions, setVersions] = useState<ExecutionRecipeTemplate[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const openHistory = async (recipe: ExecutionRecipeTemplate) => {
    setHistoryFor(recipe);
    setVersionsLoading(true);
    const { data } = await loadVersions(recipe.work_type_ref);
    setVersions(data.filter((v) => v.is_builtin || v.workspace_id === workspaceId));
    setVersionsLoading(false);
  };

  useEffect(() => {
    void fetchRecipes(workspaceId, true);
    if (workspaceId) void loadResourceTypes(workspaceId);
  }, [fetchRecipes, loadResourceTypes, workspaceId]);

  const builtins = useMemo(() => {
    const fromDb = recipes.filter((r) => r.is_builtin);
    if (fromDb.length > 0) return fromDb;
    return BUILTIN_WORK_TYPE_OPTIONS.map((b) => ({
      id: b.value,
      work_type_ref: b.value,
      name: WORK_TYPE_LABELS[b.value],
      approach_kind: b.approachKind,
      uncertainty_multiplier: b.multiplier,
      required_process_artifacts: [],
      governance_policy_refs: [],
      resource_allocations: [],
      description: null,
      is_builtin: true,
      workspace_id: null,
      active: true,
      metadata: {},
      version_number: 1,
      supersedes_id: null,
      created_at: '',
    })) as ExecutionRecipeTemplate[];
  }, [recipes]);

  const customs = useMemo(
    () => recipes.filter((r) => !r.is_builtin && r.workspace_id === workspaceId),
    [recipes, workspaceId],
  );

  /* A built-in whose ref has an active workspace row is shadowed: the workspace
     version governs resolution, so the built-in card loses its Customize action
     (edit the governing row instead) and says so. */
  const shadowedRefs = useMemo(
    () => new Set(customs.filter((c) => c.active).map((c) => c.work_type_ref)),
    [customs],
  );

  /**
   * Work types that can actually be used, and the ones among them with no
   * demand template. Custom rows are workspace-scoped and win over the
   * built-in of the same ref (resolveRecipeIdForWorkType's precedence), so a
   * built-in shadowed by a custom one is counted once, through the custom.
   */
  const activeRecipes = useMemo(() => {
    const byRef = new Map<string, ExecutionRecipeTemplate>();
    for (const r of builtins) if (r.active) byRef.set(r.work_type_ref, r);
    for (const r of customs) if (r.active) byRef.set(r.work_type_ref, r);
    return [...byRef.values()];
  }, [builtins, customs]);

  const missingDemandTemplates = useMemo(
    () => activeRecipes.filter((r) => (r.resource_allocations ?? []).length === 0),
    [activeRecipes],
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setReason('');
    setRefTouched(false);
    setShowForm(true);
  };

  const openEdit = (recipe: ExecutionRecipeTemplate) => {
    setEditingId(recipe.id);
    setForm({
      work_type_ref: recipe.work_type_ref,
      name: recipe.name,
      approach_kind: recipe.approach_kind,
      uncertainty_multiplier: recipe.uncertainty_multiplier,
      description: recipe.description ?? '',
      resource_allocations: recipe.resource_allocations ?? [],
      metadata: {
        ...recipe.metadata,
        intake_hint: (recipe.metadata?.intake_hint as string) ?? '',
      },
      active: recipe.active,
    });
    setReason('');
    setRefTouched(true);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!workspaceId) return;
    if (!form.name.trim()) {
      addNotification({ type: 'error', title: 'Name required', message: 'Enter a display name.', duration: 3000 });
      return;
    }
    if (!editingId && !form.work_type_ref.trim()) {
      addNotification({ type: 'error', title: 'ID required', message: 'Enter a work type ID.', duration: 3000 });
      return;
    }
    if (form.uncertainty_multiplier < 1) {
      addNotification({ type: 'error', title: 'Invalid multiplier', message: 'Must be at least 1.0.', duration: 3000 });
      return;
    }
    const allocations = form.resource_allocations ?? [];
    const allocError = validateResourceAllocations(
      allocations,
      getEnabledResourceTypes().map((rt) => rt.key),
    );
    if (allocError) {
      addNotification({ type: 'error', title: 'Invalid demand template', message: allocError, duration: 4000 });
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      work_type_ref: form.work_type_ref.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || null,
      resource_allocations: allocations,
      metadata: {
        ...(form.metadata ?? {}),
        intake_hint: (form.metadata?.intake_hint as string)?.trim() || undefined,
      },
    };

    const result = editingId
      ? await updateCustom(
          editingId,
          {
            name: payload.name,
            approach_kind: payload.approach_kind,
            uncertainty_multiplier: payload.uncertainty_multiplier,
            description: payload.description,
            resource_allocations: payload.resource_allocations,
            metadata: payload.metadata,
            active: payload.active,
          },
          reason,
        )
      : await createCustom(workspaceId, payload);

    setSaving(false);

    if (result.error) {
      addNotification({ type: 'error', title: 'Save failed', message: result.error, duration: 5000 });
      return;
    }

    addNotification({
      type: 'success',
      title: editingId ? 'Work type updated' : 'Work type created',
      message: `"${payload.name}" is available in item intake and planning.`,
      duration: 3000,
    });
    closeForm();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete custom work type "${name}"? Existing items keep their work_type value.`)) return;
    const { error } = await removeCustom(id);
    if (error) {
      addNotification({ type: 'error', title: 'Delete failed', message: error, duration: 5000 });
      return;
    }
    addNotification({ type: 'success', title: 'Deleted', message: `"${name}" removed from the catalog.`, duration: 3000 });
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    const { error } = await updateCustom(id, { active });
    if (error) {
      addNotification({ type: 'error', title: 'Update failed', message: error, duration: 5000 });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          icon={<Wrench className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Work Types"
          subtitle="Classify how execution work should be approached. Built-in types are global; add custom types and metadata for this workspace."
        />
        {canEdit && (
          <button type="button" onClick={openCreate} className="mt-1 btn btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Custom Type
          </button>
        )}
      </div>

      <div className="border border-[var(--slate)] bg-[var(--slate-dim)]/50 px-4 py-3 text-sm text-[var(--text-ter)]">
        A work type tells Planning how this kind of work is approached: its uncertainty multiplier, the
        documents it needs before scheduling, and whether an item is ready to schedule. Its pattern
        (Settings → Type Lists &amp; Patterns) adds its shape, workload and bar.
      </div>

      {/* The demand template is the field capacity measurement depends on, and
          it is the one nobody sees is empty — an unfilled one renders as
          silence everywhere else in the product. Counted here so the gap is a
          number on the page that owns it, not a discovery made later in a
          planning meeting. */}
      {missingDemandTemplates.length > 0 && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)]/50 px-4 py-3 text-sm text-[var(--mustard)]">
          <span className="font-medium">
            {missingDemandTemplates.length} of {activeRecipes.length} work type
            {activeRecipes.length === 1 ? '' : 's'} {missingDemandTemplates.length === 1 ? 'has' : 'have'} no
            demand template
          </span>{' '}
          ({missingDemandTemplates.map((r) => r.name).join(', ')}). A demand template is what turns
          &ldquo;this is a Novel Build&rdquo; into hours a pool has to find. Until one is set, work of that
          type carries no resource requirement: capacity coverage cannot be computed for it, and a
          commitment using it can never read as backed — only as announced.
        </div>
      )}

      {loading && recipes.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading work types…
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Built-in Types</h2>
            <div className="grid gap-3">
              {builtins.map((recipe) => (
                <RecipeCard
                  key={recipe.work_type_ref}
                  recipe={recipe}
                  readOnly={!canEdit}
                  shadowed={shadowedRefs.has(recipe.work_type_ref)}
                  onEdit={
                    /* created_at === '' marks the synthesized fallback rows used
                       before the DB catalog loads — they have no real id to revise. */
                    canEdit && recipe.created_at !== '' && !shadowedRefs.has(recipe.work_type_ref)
                      ? () => openEdit(recipe)
                      : undefined
                  }
                  resourceLabel={resourceLabel}
                />
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
              Custom Types {customs.length > 0 && `(${customs.length})`}
            </h2>
            {customs.length === 0 ? (
              <div className="border border-dashed border-[var(--rule)] px-4 py-8 text-center text-sm text-[var(--text-ter)]">
                No custom work types yet. Add one to extend the catalog for this workspace.
              </div>
            ) : (
              <div className="grid gap-3">
                {customs.map((recipe) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onEdit={canEdit ? () => openEdit(recipe) : undefined}
                    onDelete={canEdit ? () => void handleDelete(recipe.id, recipe.name) : undefined}
                    onToggleActive={canEdit ? (active) => void handleToggleActive(recipe.id, active) : undefined}
                    onHistory={() => void openHistory(recipe)}
                    resourceLabel={resourceLabel}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {showForm && canEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
              <h3 className="text-base font-semibold text-[var(--text-pri)] m-0">
                {editingId
                  ? editingIsBuiltin
                    ? 'Customize Built-in Work Type'
                    : 'Edit Custom Work Type'
                  : 'New Custom Work Type'}
              </h3>
              <button type="button" onClick={closeForm} className="p-1 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {editingIsBuiltin && (
                <p className="text-xs text-[var(--text-sec)] bg-[var(--hover-tint)] px-3 py-2 m-0">
                  Saving creates this workspace's own version of the built-in. It takes over
                  everywhere this work type is resolved here; other workspaces keep the standard one.
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Display Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((f) => ({
                      ...f,
                      name,
                      work_type_ref: !refTouched && !editingId ? slugifyWorkTypeRef(name) : f.work_type_ref,
                    }));
                  }}
                  placeholder="e.g., Compliance Remediation"
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Work Type ID *</label>
                <input
                  type="text"
                  value={form.work_type_ref}
                  disabled={!!editingId}
                  onChange={(e) => {
                    setRefTouched(true);
                    setForm((f) => ({
                      ...f,
                      work_type_ref: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                    }));
                  }}
                  placeholder="compliance_remediation"
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm font-mono disabled:bg-[var(--surface)]"
                />
                <p className="text-xs text-[var(--text-ter)] mt-1">Lowercase snake_case. Cannot match a built-in ID.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Approach Kind</label>
                  <div className="relative">
                    <select
                      value={form.approach_kind}
                      onChange={(e) => setForm((f) => ({ ...f, approach_kind: e.target.value as ApproachKind }))}
                      className="w-full appearance-none px-3 py-2 border border-[var(--rule)] text-sm pr-8"
                    >
                      {APPROACH_KIND_OPTIONS.map((k) => (
                        <option key={k} value={k}>{APPROACH_KIND_LABELS[k]}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Uncertainty Multiplier</label>
                  <input
                    type="number"
                    min={1}
                    step={0.1}
                    value={form.uncertainty_multiplier}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, uncertainty_multiplier: Number(e.target.value) || 1 }))
                    }
                    className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
                <textarea
                  value={form.description ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="When should teams use this work type?"
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Intake Hint (metadata)</label>
                <input
                  type="text"
                  value={(form.metadata?.intake_hint as string) ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      metadata: { ...f.metadata, intake_hint: e.target.value },
                    }))
                  }
                  placeholder="Short guidance shown during item creation"
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Demand template (resource allocations)
                </label>
                <p className="text-xs text-[var(--text-ter)] mb-2">
                  Baseline work units per resource type for a typical instance. Items inherit this;
                  the uncertainty multiplier is applied when planning, not stored here.
                </p>
                <div className="space-y-2">
                  {(form.resource_allocations ?? []).map((alloc, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <select
                          value={alloc.resourceType}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              resource_allocations: (f.resource_allocations ?? []).map((a, i) =>
                                i === idx ? { ...a, resourceType: e.target.value } : a,
                              ),
                            }))
                          }
                          className="w-full appearance-none px-3 py-2 border border-[var(--rule)] text-sm pr-8"
                        >
                          <option value="">Select resource type…</option>
                          {getEnabledResourceTypes().map((rt) => (
                            <option key={rt.key} value={rt.key}>{rt.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
                      </div>
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={alloc.workUnits}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            resource_allocations: (f.resource_allocations ?? []).map((a, i) =>
                              i === idx ? { ...a, workUnits: Number(e.target.value) || 0 } : a,
                            ),
                          }))
                        }
                        className="w-24 px-3 py-2 border border-[var(--rule)] text-sm"
                        aria-label="Work units"
                      />
                      <span className="text-xs text-[var(--text-ter)]">WU</span>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            resource_allocations: (f.resource_allocations ?? []).filter((_, i) => i !== idx),
                          }))
                        }
                        className="p-1.5 hover:bg-[var(--coral-dim)] text-[var(--coral)]"
                        aria-label="Remove allocation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        resource_allocations: [
                          ...(f.resource_allocations ?? []),
                          { resourceType: '', workUnits: 1 },
                        ],
                      }))
                    }
                    className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                  >
                    <Plus className="h-4 w-4" /> Add resource type
                  </button>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-[var(--text-sec)]">
                <input
                  type="checkbox"
                  checked={form.active !== false}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="rounded border-[var(--rule)]"
                />
                Available in item intake pickers
              </label>

              {editingId && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Reason for change
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this changing? (recorded in version history)"
                    className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                  />
                  <p className="text-xs text-[var(--text-ter)] mt-1">
                    Saving edits creates a new version — in-flight plans keep the version they resolved against.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--rule)]">
              <button type="button" onClick={closeForm} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={() => void handleSave()} disabled={saving} className="btn btn-primary flex items-center gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {editingId ? 'Save Changes' : 'Create Work Type'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
              <h3 className="text-base font-semibold text-[var(--text-pri)] m-0">
                Version history — {historyFor.name}
              </h3>
              <button
                type="button"
                onClick={() => setHistoryFor(null)}
                className="p-1 hover:bg-[var(--hover-tint)]"
              >
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>
            <div className="p-5">
              {versionsLoading ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading versions…
                </div>
              ) : (
                <ol className="space-y-2 m-0 p-0 list-none">
                  {versions.map((v) => (
                    <li
                      key={v.id}
                      className={` border px-3 py-2 ${
                        v.active ? 'border-[var(--slate)] bg-[var(--slate-dim)]/40' : 'border-[var(--rule)] bg-[var(--surface)]'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-pri)]">v{v.version_number}</span>
                        {v.active && (
                          <span className="text-[11px] font-medium text-[var(--text-ter)]">Active</span>
                        )}
                        {v.is_builtin && (
                          <span className="text-[11px] font-medium text-[var(--text-ter)]">Built-in</span>
                        )}
                        <span className="text-xs text-[var(--text-ter)]">
                          ×{v.uncertainty_multiplier} · {APPROACH_KIND_LABELS[v.approach_kind]}
                        </span>
                      </div>
                      {v.updated_at && (
                        <p className="text-xs text-[var(--text-ter)] mt-1 m-0">
                          {new Date(v.updated_at).toLocaleString()}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
